import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/ui/lib/utils";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect } from "react";
import { useImageSlider } from "@/lib/hooks/useImageSlider";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function ImageSlider({
  images,
  title,
  className
}) {
  const slider = useImageSlider(images.length);
  if (images.length === 0) return null;

  // Single image — no slider needed
  if (images.length === 1) {
    return /*#__PURE__*/_jsx("div", {
      className: cn("overflow-hidden", className),
      children: /*#__PURE__*/_jsx("img", {
        src: images[0].url,
        alt: images[0].altText ?? title,
        className: "h-full w-full object-cover"
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: cn("relative overflow-hidden", className),
      children: [/*#__PURE__*/_jsx("div", {
        ref: slider.emblaRef,
        className: "h-full overflow-hidden",
        children: /*#__PURE__*/_jsx("div", {
          className: "flex h-full",
          children: images.map((img, i) => /*#__PURE__*/_jsx("button", {
            type: "button",
            className: "h-full min-w-0 flex-[0_0_100%] cursor-pointer",
            onClick: () => slider.lightbox.onOpen(i),
            children: /*#__PURE__*/_jsx("img", {
              src: img.url,
              alt: img.altText ?? `${title} ${i + 1}`,
              className: "h-full w-full object-cover"
            })
          }, img.url))
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent"
      }), /*#__PURE__*/_jsx("div", {
        className: "absolute bottom-3 left-0 right-0 z-10 flex justify-center gap-1.5",
        children: images.map((_, i_0) => /*#__PURE__*/_jsx("button", {
          type: "button",
          onClick: () => slider.scrollTo(i_0),
          className: cn("h-2 rounded-full transition-all", i_0 === slider.selectedIndex ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/70")
        }, i_0))
      }), /*#__PURE__*/_jsxs("div", {
        className: "absolute right-3 top-3 z-10 rounded-full bg-black/40 px-2.5 py-1 text-xs text-white",
        children: [slider.selectedIndex + 1, " / ", images.length]
      })]
    }), slider.lightbox.open && /*#__PURE__*/_jsx(Lightbox, {
      images: images,
      index: slider.lightbox.index,
      onClose: slider.lightbox.onClose,
      onPrev: slider.lightbox.onPrev,
      onNext: slider.lightbox.onNext
    })]
  });
}

// ─── Lightbox ───────────────────────────────────────────────────────────────

function Lightbox(t0) {
  const $ = _c(31);
  const {
    images,
    index,
    onClose,
    onPrev,
    onNext
  } = t0;
  let t1;
  let t2;
  if ($[0] !== onClose || $[1] !== onNext || $[2] !== onPrev) {
    t1 = () => {
      const handleKey = e => {
        if (e.key === "Escape") {
          onClose();
        }
        if (e.key === "ArrowLeft") {
          onPrev();
        }
        if (e.key === "ArrowRight") {
          onNext();
        }
      };
      document.addEventListener("keydown", handleKey);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleKey);
        document.body.style.overflow = "";
      };
    };
    t2 = [onClose, onPrev, onNext];
    $[0] = onClose;
    $[1] = onNext;
    $[2] = onPrev;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const current = images[index];
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = /*#__PURE__*/_jsx(X, {
      className: "h-6 w-6"
    });
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onClose) {
    t4 = /*#__PURE__*/_jsx("button", {
      type: "button",
      className: "absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20",
      onClick: onClose,
      children: t3
    });
    $[6] = onClose;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onPrev) {
    t5 = e_0 => {
      e_0.stopPropagation();
      onPrev();
    };
    $[8] = onPrev;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = /*#__PURE__*/_jsx(ChevronLeft, {
      className: "h-6 w-6"
    });
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = /*#__PURE__*/_jsx("button", {
      type: "button",
      className: "absolute left-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20",
      onClick: t5,
      children: t6
    });
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = current.altText ?? "";
  let t9;
  if ($[13] !== current.url || $[14] !== t8) {
    t9 = /*#__PURE__*/_jsx("img", {
      src: current.url,
      alt: t8,
      className: "max-h-[90vh] max-w-[90vw] object-contain",
      onClick: _temp
    });
    $[13] = current.url;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== onNext) {
    t10 = e_2 => {
      e_2.stopPropagation();
      onNext();
    };
    $[16] = onNext;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = /*#__PURE__*/_jsx(ChevronRight, {
      className: "h-6 w-6"
    });
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10) {
    t12 = /*#__PURE__*/_jsx("button", {
      type: "button",
      className: "absolute right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20",
      onClick: t10,
      children: t11
    });
    $[19] = t10;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = index + 1;
  let t14;
  if ($[21] !== images.length || $[22] !== t13) {
    t14 = /*#__PURE__*/_jsxs("div", {
      className: "absolute bottom-4 left-0 right-0 text-center text-sm text-white/70",
      children: [t13, " / ", images.length]
    });
    $[21] = images.length;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== onClose || $[25] !== t12 || $[26] !== t14 || $[27] !== t4 || $[28] !== t7 || $[29] !== t9) {
    t15 = /*#__PURE__*/_jsxs("div", {
      className: "fixed inset-0 z-50 flex items-center justify-center bg-black/90",
      onClick: onClose,
      children: [t4, t7, t9, t12, t14]
    });
    $[24] = onClose;
    $[25] = t12;
    $[26] = t14;
    $[27] = t4;
    $[28] = t7;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function _temp(e_1) {
  return e_1.stopPropagation();
}